import React from "react";
import Container from "react-bootstrap/Container";
import { ApiContext } from "../common/context/ApiContext";
import { useParams, useHistory } from "react-router-dom";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import { StepNavHeader } from "../components/assignments/StepNavHeader";
import {
  StudentSelector,
  StudentSelectorMode,
} from "../components/assignments/StudentSelector";
import { TeacherSelector } from "../components/custom_sections/TeacherSelector";
import { ConfirmCustomGroup } from "../components/custom_sections/ConfirmCustomGroup";
import { useTranslation } from "react-i18next";
import { useLiveData } from "../common/utils";
import { Account, AccountContext } from "../common/context/AccountContext";
import { SchoolStudent, SchoolTeacher } from "../common/api";
import { PageContainer } from "../common/CommonStyles";

export const CustomSectionPage: React.FC = () => {
  const history = useHistory();
  const [activeStep, setActiveStep] = React.useState(0);
  const [selectedStudents, setSelectedStudents] = React.useState<
    ReadonlyMap<number, Readonly<SchoolStudent>>
  >(new Map());
  const [customSectionName, setCustomSectionName] = React.useState("");
  const [selectedTeachers, setSelectedTeachers] = React.useState<
    ReadonlyMap<number, Readonly<SchoolTeacher>>
  >(new Map());
  const {
    deleteCustomSection,
    createCustomSection,
    getCustomSection,
    updateCustomSection,
  } = React.useContext(ApiContext);

  const isNextEnabled =
    (activeStep === 0 && selectedStudents.size > 0) ||
    (activeStep === 1 && selectedTeachers.size > 0);
  const { t } = useTranslation();
  const steps = [
    t("create_instructional_group_students"),
    t("create_instructional_group_teachers"),
    t("create_instructional_group_confirm"),
  ];
  const { accountLiveData } = React.useContext(AccountContext);
  const [account, setAccount] = React.useState<Account>();
  const dataFetchedForUserVersion = React.useRef<number | null>(null);
  const fetchJobForUserVersion = React.useRef<number | null>(null);

  const { schoolId, sectionId }: { schoolId: string; sectionId?: string } =
    useParams<{
      schoolId: string;
      sectionId?: string;
    }>();

  useLiveData(
    React.useCallback(
      (account: Account) => {
        if (account.hasMaybeProfileState()) {
          setAccount(account);
        }
        if (sectionId === undefined) {
          return;
        } else {
          if (
            account.userCurrentVersion !== dataFetchedForUserVersion.current &&
            account.userCurrentVersion !== fetchJobForUserVersion.current
          ) {
            const expectedVersion = (fetchJobForUserVersion.current =
              account.userCurrentVersion);
            fetchJobForUserVersion.current = account.userCurrentVersion;
            getCustomSection(parseInt(schoolId), parseInt(sectionId!!)).then(
              (section) => {
                if (expectedVersion !== fetchJobForUserVersion.current) {
                  return;
                }
                const foundSelectedTeachers = new Map();
                section.teachers!!.forEach((teacher) => {
                  foundSelectedTeachers.set(teacher.id, teacher);
                });
                setSelectedTeachers(foundSelectedTeachers);

                const foundSelectedStudents = new Map();
                section.students!!.forEach((student) => {
                  foundSelectedStudents.set(student.id, student);
                });
                setSelectedStudents(foundSelectedStudents);
                setCustomSectionName(section.name);
                dataFetchedForUserVersion.current =
                  fetchJobForUserVersion.current;
              }
            );
          }
        }
      },
      [
        schoolId,
        sectionId,
        getCustomSection,
        setSelectedTeachers,
        setSelectedStudents,
        setCustomSectionName,
      ]
    ),
    accountLiveData
  );

  const onClickCreateCustomSection = React.useCallback(async () => {
    if (sectionId) {
      await updateCustomSection(
        parseInt(schoolId),
        parseInt(sectionId),
        customSectionName,
        Array.from(selectedStudents.keys()),
        Array.from(selectedTeachers.keys())
      );
    } else {
      await createCustomSection(
        parseInt(schoolId),
        customSectionName,
        Array.from(selectedStudents.keys()),
        Array.from(selectedTeachers.keys())
      );
    }
    history.push(`/schools/dashboard/`);
  }, [
    history,
    schoolId,
    sectionId,
    updateCustomSection,
    createCustomSection,
    customSectionName,
    selectedStudents,
    selectedTeachers,
  ]);

  const onClickDeleteCustomSection = React.useCallback(async () => {
    const confirmDelete =
      prompt(
        "Are you sure you want to delete this section?\nType 'delete' to confirm deletion"
      ) === "delete";

    if (confirmDelete) {
      deleteCustomSection(parseInt(schoolId), parseInt(sectionId!!));
      history.push(`/schools/dashboard/`);
    }
  }, [history, schoolId, sectionId, deleteCustomSection]);

  if (!account) {
    return <></>;
  }

  return (
    <PageContainer>
      <Container style={{ maxWidth: "800px" }}>
        <StepNavHeader
          isReplaceBackWithCancel={activeStep === 0}
          onBack={() => setActiveStep(activeStep - 1)}
          header={
            sectionId
              ? t("edit_instructional_group_title")
              : t("create_instructional_group_title")
          }
          isNextVisible={activeStep < steps.length - 1}
          isNextEnabled={isNextEnabled}
          onNext={() => setActiveStep(activeStep + 1)}
        />
        <Stepper activeStep={activeStep}>
          {steps.map((label, index) => {
            const stepProps: { completed?: boolean } = {};
            return (
              <Step key={label} {...stepProps}>
                <StepLabel>{label}</StepLabel>
              </Step>
            );
          })}
        </Stepper>
      </Container>
      {activeStep === 0 && (
        <StudentSelector
          account={account}
          mode={StudentSelectorMode.CUSTOM_SECTION}
          selectedStudents={selectedStudents}
          setSelectedStudents={setSelectedStudents}
          alreadyAssignedStudentIds={[]}
        />
      )}
      {activeStep === 1 && (
        <TeacherSelector
          account={account}
          selectedTeachers={selectedTeachers}
          setSelectedTeachers={setSelectedTeachers}
        />
      )}
      {activeStep === 2 && (
        <ConfirmCustomGroup
          customSectionName={customSectionName}
          setCustomSectionName={setCustomSectionName}
          selectedStudents={Array.from(selectedStudents.values())}
          selectedTeachers={Array.from(selectedTeachers.values())}
          onClickConfirm={() => onClickCreateCustomSection()}
          showDelete={sectionId !== undefined}
          onClickDelete={() => onClickDeleteCustomSection()}
        />
      )}
    </PageContainer>
  );
};
